<template>
  <q-list
    id="generalAnchor"
    dense
    class="q-mb-lg"
  >
    <sticky-header>General</sticky-header>

    <detail-item
      :name-spacing="nameSpacing"
      icon="mdi-pencil-outline"
      with-input
      required
      name="Title"
    >
      <q-input
        v-model="event.title"
        dense
        outlined
        bottom-slots
        hide-bottom-space
        :error="!!errors['title']"
        :error-message="!!errors['title'] ? errors['title'][0] : null"
        class="required-astrx-container"
      />
    </detail-item>

    <detail-item
      :name-spacing="nameSpacing"
      name="Description"
      with-input
    >
      <q-input
        v-model="event.description"
        dense
        autogrow
        outlined
        bottom-slots
        hide-bottom-space
        :error="!!errors['description']"
        :error-message="!!errors['description'] ? errors['description'][0] : null"
      />
    </detail-item>

    <detail-item
      :name-spacing="nameSpacing"
      :error="!!errors['color']"
      :class="{'bg-blue-grey-1': selectedTab === 'color'}"
      name="Colour"
      clickable
      with-input
      view-more
      icon="mdi-format-color-fill"
      @click="$emit('tab', 'color')"
    >
      <color-field
        v-model="event.color"
        disable
        class="inline-block"
        :error-message="!!errors['color'] ? errors['color'][0] : null"
      />
    </detail-item>

    <detail-item
      :name-spacing="nameSpacing"
      with-input
      icon="mdi-book-open-page-variant-outline"
      required
      name="Reference Number"
    >
      <q-input
        v-model="event.reference_id"
        dense
        outlined
        bottom-slots
        hide-bottom-space
        :error="!!errors['reference_id']"
        :error-message="!!errors['reference_id'] ? errors['reference_id'][0] : null"
        @update:model-value="$emit('updateReference')"
      />
    </detail-item>

    <detail-item
      v-if="mode === 'add'"
      :name-spacing="nameSpacing"
      name="Note"
      with-input
      icon="mdi-note-outline"
    >
      <q-input
        v-model="event.note"
        outlined
        dense
        placeholder="Enter Note..."
        clearable
        hint="You can add more notes after saving this appointment."
        :error="!!errors['note']"
        :error-message="!!errors['note'] ? errors['note'][0] : null"
      />
    </detail-item>
  </q-list>

  <q-list
    class="q-pb-md"
    dense
  >
    <sticky-header>Time & Location</sticky-header>

    <detail-item
      :name-spacing="nameSpacing"
      :with-input="31"
      name="Status"
      icon="mdi-bookmark-check"
    >
      <StatusInput
        v-model="event.status"
        :edit="mode === 'edit'"
        @update:model-value="onMarkScheduled"
      />
    </detail-item>

    <detail-item
      v-if="event.status !== 'UNSCHEDULED'"
      :name-spacing="nameSpacing"
      icon="mdi-calendar-today"
      with-input
      required
      name="Start Date"
    >
      <div class="row q-col-gutter-xs">
        <div class="col-12 col-sm-5">
          <date-picker
            v-model="startDate"
            dense
            outlined
            mobile-label="Select Start Date"
            :error="!!errors['start_date']"
            :error-message="!!errors['start_date'] ? errors['start_date'][0] : null"
            @update:model-value="onUpdateStartDate"
            @mobile-input="onStartDateMobileInput"
          />
        </div>
        <div
          class="col-12 col-sm-3"
        >
          <time-picker
            v-if="!event.all_day"
            ref="startTime"
            v-model="startTime"
            :error="!!errors['start_date']"
            dense
            outlined
            mobile-label="Select Start Time"
          />
        </div>
        <div v-if="event.status !== 'UNSCHEDULED'" class="col-12 col-sm-3 q-pl-sm-md">
          <q-checkbox
            v-model="event.all_day"
            label="All Day"
          />
        </div>
      </div>
    </detail-item>

    <detail-item
      v-if="event.status !== 'UNSCHEDULED'"
      :name-spacing="nameSpacing"
      icon="mdi-calendar"
      with-input
      required
      name="End Date"
    >
      <div class="row q-col-gutter-xs">
        <div class="col-12 col-sm-5">
          <date-picker
            v-model="endDate"
            dense
            outlined
            mobile-label="Select End Date"
            :error="!!errors['end_date']"
            :error-message="!!errors['end_date'] ? errors['end_date'][0] : null"
            @update:model-value="onUpdateEndDate"
            @mobile-input="onEndDateMobileInput"
          />
        </div>
        <div
          v-if="!event.all_day"
          class="col-12 col-sm-3"
        >
          <time-picker
            ref="endTime"
            v-model="endTime"
            :error="!!errors['start_date']"
            dense
            outlined
            mobile-label="Select End Time"
          />
        </div>
      </div>

      <q-item-label
        v-if="!!minutesBetweenDates"
        class="q-mt-sm"
        caption
      >
        {{ minutesToStr(minutesBetweenDates) }} length
      </q-item-label>

      <q-item-label
        v-if="isOutsideBusinessHours"
        class="text-orange-10 text-bold q-pt-sm"
      >
        The start/end date may fall outside of working hours
      </q-item-label>
    </detail-item>

    <detail-item
      v-if="event.status === 'UNSCHEDULED'"
      :name-spacing="nameSpacing"
      name="Estimated Date"
      icon="mdi-calendar-week"
      with-input
      required
    >
      <date-picker
        v-model="startDate"
        dense
        outlined
        :error="!!errors['start_date']"
        :error-message="!!errors['start_date'] ? errors['start_date'][0] : null"
        @update:model-value="onUpdateStartDate"
      />
      <div class="q-field__bottom">
        Unscheduled events are placed in the sidebar and can be added to your calendar once the date has been confirmed.
      </div>
    </detail-item>

    <RecurringRuleDetailItem
      :event="event"
      :start-date="startDate"
      :start-time="startTime"
      :mode="mode"
      :has-updated-rule="hasUpdatedRule"
      :has-deleted-rule="hasDeletedRule"
      :has-future-rule="hasFutureRule"
      :name-spacing="nameSpacing"
      style="margin: 16px 0 !important;"
    />

    <detail-item
      :name-spacing="nameSpacing"
      with-input
      name="Location"
      icon="mdi-map-marker"
    >
      <address-field
        :address="event.address"
        :error-message="!!errors['address'] ? errors['address'][0] : null"
        :search-loading="loadingSuggestions"
        :suggestions="suggestedAddresses"
        dialog-title="Set Location"
        outlined
        dense
        single-line
        auto-clear
        suggestions-label="Suggestions based on selected clients"
        allow-refresh-suggestions
        @load-suggestions="loadSuggestions"
        @refresh-suggestions="refreshSuggestions"
        @create="addNewAddress"
        @clear="event.address = null"
      />
    </detail-item>

    <detail-item
      v-if="!company.hide_timezone_options || (event.timezone !== company.timezone)"
      name="Timezone"
      with-input
      icon="mdi-map-clock"
      :name-spacing="nameSpacing"
    >
      <timezones
        v-model="event.timezone"
        outlined
        dense
        :error="!!errors['timezone']"
        hint="The appointment timezone is important to ensure reminders are sent out at the correct local time"
      />
    </detail-item>
  </q-list>
</template>

<script>
import datetimeMixin from '../../mixins/datetimeMixin'
import StatusInput from '../../status/Input'
import RecurringRuleDetailItem from 'components/app/calendar/partials/edit/panels/partials/RecurringRuleDetailItem'
import { getAddressPayload } from 'components/partials/PhoneAddressEmail'

export default {
  components: {
    StatusInput, RecurringRuleDetailItem
  },
  mixins: [datetimeMixin],
  props: {
    event: {
      required: true,
      type: Object
    },
    mode: {
      type: String,
      default: 'add'
    },
    errors: {
      type: Object,
      default: () => {
        return {}
      }
    },
    businessHours: {
      type: Object,
      default: null
    },
    hasUpdatedRule: {
      type: Boolean,
      default: false
    },
    hasDeletedRule: {
      type: Boolean,
      default: false
    },
    hasFutureRule: {
      type: Boolean,
      default: false
    },
    nameSpacing: {
      type: Number,
      required: true
    },
    selectedTab: {
      type: String,
      default: null
    }
  },
  emits: ['tab', 'updateReference'],
  computed: {
    isOutsideBusinessHours () {
      if (this.event.status === 'UNSCHEDULED' || this.event.all_day) {
        return false
      }
      return this.baseIsOutsideBusinessHours(this.businessHours)
    },
    loadingSuggestions () {
      if (this.event.clients.length < 1) {
        return false
      }
      return this.$store.getters['clientSuggestionLoader/isLoadingAddresses'](this.event.clients[0].id)
    },
    suggestedAddresses () {
      if (this.event.clients.length < 1) {
        return []
      }
      return this.$store.getters['clientSuggestionLoader/getAddresses'](this.event.clients[0].id)
    },
    minutesBetweenDates () {
      if (!this.event.start_date || !this.event.end_date || this.event.status === 'UNSCHEDULED' || this.event.all_day) {
        return null
      }
      return this.luxon.fromSQL(this.event.end_date).diff(this.luxon.fromSQL(this.event.start_date), 'minutes').toObject().minutes
    }
  },
  methods: {
    addNewAddress () {
      this.event.address = getAddressPayload()
    },
    onMarkScheduled (val) {
      if (val === 'UNSCHEDULED') {
        this.event.recurring_status = 'UNSCHEDULED'
      }
    },
    loadSuggestions () {
      if (this.event.clients.length < 1) {
        return
      }
      this.$store.dispatch('clientSuggestionLoader/loadAddresses', this.event.clients[0].id)
    },
    refreshSuggestions () {
      if (this.event.clients.length < 1) {
        return
      }
      this.$store.commit('clientSuggestionLoader/removeClientAddresses', this.event.clients[0].id)
      this.loadSuggestions()
    },
    onStartDateMobileInput () {
      try {
        this.$refs.startTime.focus()
      }
      catch (err) {}
    },
    onEndDateMobileInput () {
      try {
        this.$refs.endTime.focus()
      }
      catch (err) {}
    }
  }
}
</script>
